package ai.jessie.web.profile

import ai.jessie.shared.profile.OwnProfileResponse
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.theme.AlertListStyle
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStackStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.MemberNameStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.PersonStyle
import ai.jessie.web.theme.ProfileAvatarStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.card
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// UC-026 (`docs/design/screens/18-EinstellungenProfil.png`): the scopes as read-only cards, the
// form in their place, the review as a card. The page title and side navigation live in the page.
@Composable
fun ownProfile(state: ProfileUiState, actions: ProfileActions) {
    val focusReturn = remember { FocusReturn() }
    state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
    if (state.error?.authenticationRequired == true) return
    if (state.busy) status("Wird geladen oder gespeichert …")
    state.notice?.let { status(it) }
    profileProblems(state.problems)
    profileConflictList(state.conflicts)
    val current = state.current
    when {
        state.review != null -> profileReview(state, actions, focusReturn)
        state.draft != null -> profileForm(state, actions, focusReturn)
        else -> {
            current?.let { profileView(it, state, actions, focusReturn) }
            Div(attrs = RowStyle.toAttrs()) {
                themedButton(
                    "Profil erneut laden",
                    actions.refresh,
                    ButtonVariant.GHOST,
                    disabled = state.busy,
                )
            }
        }
    }
}

// Returns keyboard focus to the section heading after the review closes, not on first render.
internal class FocusReturn {
    var pending = false
}

@Composable
private fun status(text: String) {
    P(attrs = StatusStyle.toAttrs { attr("role", "status") }) { Text(text) }
}

@Composable
internal fun profileHeading(title: String, state: ProfileUiState, focusReturn: FocusReturn) {
    H2(
        attrs = {
            id(PROFILE_TITLE_ID)
            attr("tabindex", "-1")
        }
    ) {
        Text(title)
    }
    LaunchedEffect(Unit) {
        // While a problem is shown, the alert or the marked field keeps focus.
        if (focusReturn.pending && state.error == null && !AppGlobals.isExporting)
            (document.getElementById(PROFILE_TITLE_ID) as? HTMLElement)?.focus()
        focusReturn.pending = false
    }
}

// A1: every invalid field with its correction; marked fields refer to this list.
@Composable
private fun profileProblems(problems: Map<String, String>) {
    if (problems.isEmpty()) return
    Ul(attrs = AlertListStyle.toAttrs { id(PROFILE_PROBLEMS_ID) }) {
        problems.forEach { (field, correction) -> Li { Text("${fieldLabel(field)}: $correction") } }
    }
}

// A5: stored and intended values stay visible while the user re-enters changes.
@Composable
private fun profileConflictList(conflicts: List<ProfileConflict>) {
    if (conflicts.isEmpty()) return
    Section(attrs = AlertStyle.toAttrs { attr("aria-labelledby", "profile-conflicts-title") }) {
        H2(attrs = { id("profile-conflicts-title") }) { Text("Vergleich mit dem neueren Stand") }
        Ul(attrs = SubListStyle.toAttrs()) {
            conflicts.forEach { row ->
                Li {
                    B { Text("Konflikt – ") }
                    Text(
                        "${fieldLabel(row.field)}: jetzt gespeichert " +
                            "${shown(row.field, row.current)} · dein Wert " +
                            shown(row.field, row.intended)
                    )
                }
            }
        }
    }
}

// Step 2 and A2. Only what the server returned is shown; no membership value is retained. The
// edit action sits in the footer of the last card.
@Composable
private fun profileView(
    current: OwnProfileResponse,
    state: ProfileUiState,
    actions: ProfileActions,
    focusReturn: FocusReturn,
) {
    val values = current.draft().fields
    val membership = current.membership
    val edit: @Composable () -> Unit = {
        themedButton("Profil bearbeiten", actions.edit, ButtonVariant.DARK, disabled = state.busy)
    }
    Div(attrs = CardStackStyle.toAttrs()) {
        Div(attrs = CardStyle.toAttrs()) {
            Div(attrs = CardHeadStyle.toAttrs()) {
                profileHeading("Dein Profil", state, focusReturn)
                P { Text("Global – gilt in ganz Jessie") }
            }
            Div(attrs = CardBodyStyle.toAttrs()) {
                avatarBlock(current)
                definitions(values.filterKeys { it.startsWith("global.") })
                if (membership == null)
                    P(attrs = HintStyle.toAttrs()) {
                        Text(
                            "Keine Organisation ausgewählt. Organisationsbezogene Angaben sind " +
                                "nur mit einer aktiven Mitgliedschaft verfügbar."
                        )
                    }
            }
            if (membership == null) Div(attrs = CardFootStyle.toAttrs()) { edit() }
        }
        if (membership != null)
            card("Nur in der Organisation «${membership.organisationName}»", footer = edit) {
                definitions(values.filterKeys { it.startsWith("membership.") }) {
                    // BR-187: the override when present, otherwise the global display name.
                    definition("Dort angezeigt als", membership.effectiveDisplayName)
                }
            }
    }
}

// Initials of the stored names, the display name and the verified email (read-only). No picture:
// the contract has none.
@Composable
private fun avatarBlock(current: OwnProfileResponse) {
    Div(attrs = PersonStyle.toAttrs()) {
        Span(attrs = ProfileAvatarStyle.toAttrs { attr("aria-hidden", "true") }) {
            Text(
                listOf(current.global.firstName, current.global.lastName)
                    .mapNotNull { it.trim().firstOrNull()?.uppercase() }
                    .joinToString("")
            )
        }
        Div(attrs = MemberNameStyle.toAttrs()) {
            Span(attrs = TableStrongStyle.toAttrs()) { Text(current.global.displayName) }
            Span(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) { Text(current.verifiedEmail) }
        }
    }
}

@Composable
private fun definitions(values: Map<String, String>, extra: @Composable () -> Unit = {}) {
    TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
        values.forEach { (field, value) -> definition(fieldLabel(field), shown(field, value)) }
        extra()
    }
}

@Composable
private fun definition(term: String, value: String) {
    TagElement<HTMLElement>("dt", null) { Text(term) }
    TagElement<HTMLElement>("dd", null) { Text(value) }
}

internal const val PROFILE_TITLE_ID = "profile-title"
internal const val PROFILE_PROBLEMS_ID = "profile-problems"
